/**
 * Version Release History
 * 
 * To add a new version:
 * 1. Add a new object at the TOP of this array (index 0)
 * 2. Move the current latest version down (remove 'status: Latest' from it)
 * 3. Use semantic versioning: v1.4.0, v1.5.0, v2.0.0, etc.
 * 4. Choose a descriptive codename
 * 5. Set the date to the release month/year
 * 6. Add 'status: Latest' to the new version
 * 7. List 3-6 key highlights in the highlights array
 * 
 * Version Object Structure:
 * {
 *   version: 'v1.4.0',           // Semantic version
 *   codename: 'Feature Name',     // Descriptive codename
 *   date: 'Nov 2025',             // Release month/year
 *   status: 'Latest',             // Only on the most recent version
 *   highlights: [                 // Array of feature descriptions
 *     'Feature description 1',
 *     'Feature description 2',
 *   ],
 * }
 */
const releases = [
  {
    version: 'v1.6.0',
    codename: 'Clone & Guard',
    date: 'Jan 2026',
    status: 'Latest',
    highlights: [
      'Box cloning for organizers: copy members, schedule, desired payouts, and custom dates with a new start date',
      'Schedule guardrail blocks saves when monthly payouts exceed total monthly contributions',
      'Schedule Builder allows multiple members per slot with per-member desired payouts and in-slot stacking',
      'Rules page added with all applied guardrails and policies; How-To updated with cloning and stacking scenarios',
      'Navigation adds Rules page; landing updated for cloning and guardrail messaging',
    ],
  },
  {
    version: 'v1.5.0',
    codename: 'Fair Schedule',
    date: 'Dec 2025',
    highlights: [
      'Multi-payout schedule engine with enforced even distribution and explicit assignments',
      'Schedule Builder saves configs to the backend and reloads them with inline validation',
      'Payout Schedule table now shows multiple slots per month and organizer error guidance',
      'Box creation/edit flows include payouts-per-month so defaults are stored',
      'How-to guide updated with concrete scheduling scenarios and user flows',
    ],
  },
  {
    version: 'v1.4.0',
    codename: 'Management & Control',
    date: 'Nov 2025',
    highlights: [
      'Complete member management: edit details, promote to organizer, and remove members with required reasons',
      'Box editing and deletion: update box details or delete boxes with audit trail',
      'Ledger entry voiding: mark entries as void with reasons instead of deleting',
      'Comprehensive audit logging: track all administrative actions for every box',
      'Responsive improvements: horizontal scrolling for tables and tab navigation on mobile',
      'Consistent form styling: unified design across all modals and forms',
    ],
  },
  {
    version: 'v1.3.0',
    codename: 'Guided Editing',
    date: 'Feb 2025',
    highlights: [
      'New manage-member modal with edit, promote, and remove controls in one place',
      'Single "Edit" button replaces hidden action menu for faster access',
      'Version history page added so teams can track what changed',
    ],
  },
  {
    version: 'v1.2.0',
    codename: 'Invite Assurance',
    date: 'Jan 2025',
    highlights: [
      'Improved invitation resend + delete flows to keep member lists clean',
      'Clearer error messages when an invite cannot be delivered',
      'Activity tab now groups events by box for quicker scanning',
    ],
  },
  {
    version: 'v1.1.0',
    codename: 'Cycle Clarity',
    date: 'Dec 2024',
    highlights: [
      'Payout schedule cards redesigned for readability on mobile',
      'Added contribution amounts to dashboard stats tiles',
      'Refined notification copy for ledger changes',
    ],
  },
]

export default function Versions() {
  const latest = releases[0]

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto py-8 space-y-10">
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-primary-600 via-primary-500 to-primary-400 text-white shadow-xl">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_#ffffff_0,_transparent_35%)] pointer-events-none" />
          <div className="relative px-6 py-10 sm:px-10">
            <p className="text-sm uppercase tracking-wide font-semibold">Release notes</p>
            <div className="mt-3 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold">BoxHand versions</h1>
                <p className="mt-2 text-primary-50">
                  Stay current on what changed in each release—feature upgrades, fixes, and UI updates.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-white/10 px-4 py-3 shadow-inner">
                  <div className="text-xs uppercase tracking-widest text-primary-100">Current</div>
                  <div className="text-2xl font-bold leading-tight">{latest.version}</div>
                  <div className="text-sm text-primary-100">{latest.date}</div>
                </div>
                <div className="rounded-full bg-white/20 px-3 py-2 text-sm font-semibold text-white">{latest.codename}</div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white border border-gray-100 rounded-3xl shadow-sm p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Latest release</h2>
              <p className="mt-1 text-gray-600">What’s new in {latest.version} ({latest.codename}).</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-sm font-semibold text-green-700">
                Ready to use
              </span>
              <span className="text-sm text-gray-500">{latest.date}</span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {latest.highlights.map((item) => (
              <div key={item} className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
                <div className="text-sm font-semibold text-gray-900">Feature</div>
                <p className="mt-2 text-gray-600">{item}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-900">Previous versions</h3>
            <p className="text-sm text-gray-500">Highlights from earlier releases.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {releases.slice(1).map((release) => (
              <div
                key={release.version}
                className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-primary-700">{release.version}</div>
                    <h4 className="text-lg font-bold text-gray-900">{release.codename}</h4>
                  </div>
                  <span className="text-xs uppercase tracking-wide text-gray-500">{release.date}</span>
                </div>
                <ul className="mt-3 space-y-2 text-gray-600">
                  {release.highlights.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 h-2 w-2 rounded-full bg-primary-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}
